import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Search, 
  Mail, 
  Phone, 
  X,
  Loader,
  Truck,
  Edit2,
  Users
} from 'lucide-react';
import EditCustomerModal from '../components/Customers/EditCustomerModal';
import { Can } from '../components/Auth/Can';
import {
  selectFilteredCustomers,
  selectCustomersFilters,
  selectCustomersLoading,
  selectCustomersError,
  setFilters,
  clearFilters,
  fetchCustomers,
  updateCustomer
} from '../store/slices/customersSlice';

const Customers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const customers = useSelector(selectFilteredCustomers);
  const filters = useSelector(selectCustomersFilters);
  const loading = useSelector(selectCustomersLoading);
  const error = useSelector(selectCustomersError);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const clientTypeOptions = [
    {
      value: 'all',
      label: 'Todos',
      description: 'Ver todos los clientes'
    },
    {
      value: 'exportacion',
      label: 'Exportación',
      description: 'USA y Canadá'
    },
    {
      value: 'nacional',
      label: 'Nacional',
      description: 'Chile'
    }
  ];

  // Cargar clientes al montar el componente
  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
  };

  const handleCloseModal = () => {
    setEditingCustomer(null);
  };

  const handleSaveCustomer = async (customerId, customerData) => {
    await dispatch(updateCustomer({ id: customerId, customerData }));
  };


  return (
    <div className="customers-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('customers.title')}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            {t('customers.subtitle')}
          </p>
        </div>
      </div>

      {/* Client Type Selector */}
      <div className="card client-type-selector-card">
        <div className="client-type-selector-header">
          <Users size={18} />
          <span>Tipo de Cliente</span>
        </div>
        <div className="client-type-selector-buttons">
          {clientTypeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`client-type-button ${filters.clientType === option.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('clientType', option.value)}
            >
              <div className="client-type-button-icon">
                <Users size={18} />
              </div>
              <div className="client-type-button-text">
                <span className="client-type-label">{option.label}</span>
                <span className="client-type-description">{option.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder={`${t('common.search')} clientes...`}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          {(filters.search || filters.clientType !== 'all') && (
            <div className="filter-actions">
              <button 
                className="btn btn-outline"
                onClick={handleClearFilters}
              >
                <X size={20} />
                Limpiar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p className="text-gray-600">
          Mostrando {customers.length} cliente{customers.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <div className="alert alert-error">
            <span>Error: {error}</span>
            <button 
              className="btn btn-sm btn-outline"
              onClick={() => dispatch(fetchCustomers())}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Customers Table */}
      <div className="card">
        {loading ? (
          <div className="loading-state">
            <Loader className="animate-spin" size={32} />
            <p className="text-gray-600 mt-4">Cargando clientes...</p>
          </div>
        ) : customers.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('customers.name')}</th>
                  <th>{t('customers.contact')}</th>
                  <th>Dirección</th>
                  <th>Transporte</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <div className="customer-info">
                        <div className="customer-avatar">
                          {customer.nombre?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <div className="font-medium">{customer.nombre}</div>
                          <div className="text-sm text-gray-500">ID: {customer.codigo_cliente || customer.id}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div className="contact-item">
                          <Mail size={14} />
                          <span>{customer.email_contacto || 'No disponible'}</span>
                        </div>
                        <div className="contact-item">
                          <Phone size={14} />
                          <span>{customer.telefono || 'No disponible'}</span>
                        </div>
                        <div className="text-sm font-medium">{customer.nombre_contacto || 'No disponible'}</div>
                      </div>
                    </td>
                    <td>
                      <div className="address-info">
                        {customer.direccion && <div className="address-line">{customer.direccion}</div>}
                        {customer.ciudad && <div className="address-line">{customer.ciudad}</div>}
                        {customer.estado && <div className="address-line">{customer.estado}</div>}
                        {customer.codigo_postal && <div className="address-line">{customer.codigo_postal}</div>}
                        {customer.pais && <div className="address-line">{customer.pais}</div>}
                        {!customer.direccion && !customer.ciudad && !customer.estado && !customer.codigo_postal && !customer.pais && (
                          <div className="text-sm text-gray-500">No disponible</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="transport-info">
                        {customer.transportes?.nombre ? (
                          <div className="transport-details">
                            <div className="transport-item">
                              <Truck size={14} className="text-gray-500" />
                              <span className="text-sm">{customer.transportes.nombre}</span>
                            </div>
                            <span className={`transport-badge ${customer.transportes.tipo === 'exportacion' ? 'badge-exportacion' : 'badge-nacional'}`}>
                              {customer.transportes.tipo === 'exportacion' ? 'Exportación' : 'Nacional'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No disponible</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Can I="update" this="customers:management">
                          <button
                            className="btn-icon btn-icon-edit"
                            onClick={() => handleEditCustomer(customer)}
                            title="Editar cliente"
                          >
                            <Edit2 size={18} />
                          </button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mt-4">
              No se encontraron clientes
            </h3>
            <p className="text-gray-500 mt-2">
              Ajusta los filtros o agrega nuevos clientes
            </p>
          </div>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={handleCloseModal}
          onSave={handleSaveCustomer}
        />
      )}

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--spacing-6);
        }

        .client-type-selector-card {
          margin-bottom: var(--spacing-6);
        }

        .client-type-selector-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          margin-bottom: var(--spacing-4);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .client-type-selector-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: var(--spacing-3);
        }

        .client-type-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          background: white;
          text-align: left;
          transition: all 0.2s ease-in-out;
          cursor: pointer;
        }

        .client-type-button:hover {
          border-color: var(--primary-blue);
          box-shadow: 0 10px 30px -12px rgba(59, 130, 246, 0.4);
        }

        .client-type-button.active {
          border-color: var(--primary-blue);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.02));
        }

        .client-type-button-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background-color: rgba(59, 130, 246, 0.12);
          color: var(--primary-blue);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .client-type-button.active .client-type-button-icon {
          background-color: var(--primary-blue);
          color: white;
        }

        .client-type-button-text {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }

        .client-type-label {
          font-weight: 600;
          color: var(--gray-900);
          font-size: var(--font-size-lg);
        }

        .client-type-description {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }
        
        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-4);
          margin-bottom: var(--spacing-4);
        }
        
        .search-bar {
          position: relative;
          flex: 1;
          max-width: 400px;
        }
        
        .search-bar svg {
          position: absolute;
          left: var(--spacing-3);
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400);
        }
        
        .search-input {
          width: 100%;
          padding: var(--spacing-3) var(--spacing-3) var(--spacing-3) var(--spacing-10);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: var(--font-size-base);
          transition: border-color 0.2s ease-in-out;
        }
        
        .search-input:focus {
          outline: none;
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgb(59 130 246 / 0.1);
        }
        
        .filter-actions {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .results-summary {
          margin-bottom: var(--spacing-4);
        }
        
        .table-container {
          overflow-x: auto;
        }
        
        .table th:nth-child(1) {
          width: 20%;
        }
        
        .table th:nth-child(2) {
          width: 25%;
        }
        
        .table th:nth-child(3) {
          width: 25%;
        }
        
        .table th:nth-child(4) {
          width: 20%;
        }
        
        .table th:nth-child(5) {
          width: 10%;
        }
        
        .customer-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }
        
        .customer-avatar {
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
          background-color: var(--primary-blue);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: var(--font-size-sm);
          flex-shrink: 0;
        }
        
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .contact-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }
        
        .address-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }
        
        .address-line {
          font-size: var(--font-size-sm);
          color: var(--gray-700);
          line-height: 1.4;
        }
        
        .transport-info {
          display: flex;
          align-items: center;
        }
        
        .transport-details {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-2);
        }
        
        .transport-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
        }
        
        .transport-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          width: fit-content;
        }
        
        .badge-exportacion {
          background-color: rgba(59, 130, 246, 0.1);
          color: #2563eb;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }
        
        .badge-nacional {
          background-color: rgba(34, 197, 94, 0.1);
          color: #16a34a;
          border: 1px solid rgba(34, 197, 94, 0.2);
        }
        
        .action-buttons {
          display: flex;
          gap: var(--spacing-2);
          align-items: center;
        }
        
        .btn-icon {
          background: none;
          border: none;
          padding: var(--spacing-2);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-icon-edit {
          color: var(--primary-blue);
        }
        
        .btn-icon-edit:hover {
          background-color: rgba(59, 130, 246, 0.1);
        }
        
        .empty-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }
        
        .empty-icon {
          font-size: 4rem;
          margin-bottom: var(--spacing-4);
        }
        
        .loading-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .error-message {
          margin-bottom: var(--spacing-4);
        }
        
        .alert {
          padding: var(--spacing-4);
          border-radius: var(--radius-md);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .alert-error {
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-4);
          }

          .client-type-selector-buttons {
            grid-template-columns: 1fr;
          }
          
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-bar {
            max-width: none;
          }
          
          .table {
            font-size: var(--font-size-sm);
          }
          
          .customer-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-2);
          }
          
          .customer-avatar {
            width: 32px;
            height: 32px;
            min-width: 32px;
            min-height: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default Customers;
